/* eslint-disable */
 /*
 * 阿里云OSS - 文件上传/下载(列表导出除外)
 * doc https://help.aliyun.com/document_detail/64041.html?spm=a2c4g.11186623.6.1241.35715966FGsj8i
 * OSS只支持20M以内的图片裁剪
 */
import OSS from 'ali-oss';
import dayjs from 'dayjs';
import { commonApis, } from '@/api';
import { createUuid, } from '@/utils';

// Oss SDK单例
class Oss {
    // 获取阿里云OSS凭证
    static async queryOssToken(authType) {
        const { instance, } = Oss;

        try {
            const { data, } = await commonApis.getOssToken({ authType, });
            instance[authType].token = {
                ...data,
                // 记录获取token时间(本地时间戳)
                timeStamp: dayjs().valueOf(),
            };
        } catch (err) {
            throw new Error(err);
        }
    }

    // 创建OSS client
    static async createClient(authType) {
        const { instance, } = Oss;

        try {
            // 懒加载OSS凭证
            if (!instance[authType].token) {
                await Oss.queryOssToken(authType);
            } else if (dayjs().valueOf() - instance[authType].token.timeStamp >= 20 * 60 * 1000) {
                // token失效自动更新, 默认有效时间20分钟
                await Oss.queryOssToken(authType);
                instance[authType].client = null;
            }

            // 懒得实例化
            if (!instance[authType].client) {
                const opt = {
                    accessKeyId: instance[authType].token.accessKeyId,
                    accessKeySecret: instance[authType].token.accessKeySecret,
                    stsToken: instance[authType].token.securityToken,
                    bucket: instance[authType].token.bucket,
                    endpoint: instance[authType].token.endPoint,
                    timeout: 150 * 1000,
                };
                instance[authType].client = new OSS(opt);
            }
        } catch (err) {
            throw new Error(err);
        }

        return instance[authType].client;
    }

    constructor() {
        if (!Oss.instance) {
            // 公共读
            this.PUBLIC = {
                moduleMap: [
                    'avatar',
                    'shop',
                    'feedback',
                ],
                // token一小时自动失效
                token: null,
                client: null,
            };
            // 私有读
            this.PRIVATE = {
                moduleMap: [
                    'maintain',
                    'equity',
                    'workOrder',
                    'autoInsurance',
                ],
                // token一小时自动失效
                token: null,
                client: null,
            };

            Oss.instance = this;
        }
        return Oss.instance;
    }

    /**
     * 以下为对外暴露的实例api
     */

    /**
     *  文件上传
     *  @param  files       Array/FileList  File文件列表或者Blob列表
     *  @param  moduleName  String          模块名，会自动判断上传路径
     *
     *                                      公共读
     *                                          1. avatar     头像
     *                                          2. shop       店铺设置
     *                                          3. feedback   意见反馈
     *
     *                                      私有读
     *                                          1. maintain   预约/维护保养
     *
     *  @return resultList  Array           返回阿里云OSS上传结果对象 (结果对象也可能是失败结果)
     */
    uploadFiles(files, moduleName) {
        return new Promise(async (resolve, reject) => {
            try {
                if (!files || !files.length) {
                    reject(new Error('文件列表不能为空'));
                    return;
                }
                if (!moduleName) {
                    reject(new Error('模块名不能为空'));
                    return;
                }

                /**
                 * 上传域
                 *
                 *  PUBLIC  - 私有写公共读存储空间
                 *  PRIVATE - 私有写私有读存储空间
                 */
                const authType = this.PRIVATE.moduleMap.includes(moduleName) ? 'PRIVATE' : 'PUBLIC';
                const client = await Oss.createClient(authType);

                /**
                 * 上传目录
                 */
                // 一级目录(模块名)
                const firstPath = `/${moduleName}/`;
                // 二级目录：当前年月(201912)
                const secondPath = dayjs().format('YYYYMM');
                const rootPath = firstPath + secondPath;

                const promiseList = [];
                const resultList = [];
                for (let i = 0, len = files.length; i < len; i++) {
                    let file = files[i];
                    // 文件扩展名
                    let ext = '';
                    if (file.name && /\.([0-9a-z]+)(?:[?#]|$)/i.test(file.name)) {
                        // eslint-disable-next-line
                        ext = file.name.match(/\.([0-9a-z]+)(?:[?#]|$)/i)[1];
                    }
                    const filepath = `${rootPath}/${createUuid()}.${ext}`;
                    // File也需要转成原始Blob
                    if (Object.prototype.toString.call(file) !== '[object Blob]') {
                        file = new Blob([
                            file,
                        ], {
                            type: file.type || 'application/octet-stream',
                        });
                    }
                    promiseList.push(client.put(filepath, file)
                        .then(res => {
                            resultList[i] = res;
                        })
                        .catch(err => {
                            resultList[i] = err;
                    }));
                }

                Promise.all(promiseList).finally(() => {
                    resolve(resultList);
                });
            } catch (err) {
                reject(err);
            }
        });
    }

    /**
     *  文件下载
     *
     *  @param  name        String          阿里云OSS文件name (例: 'maintain/201912/xxx.png')
     *  @param  authType    String          (选填, 默认值为'PUBLIC') 存储空间类型 (PUBLIC - 公共读; PRIVATE - 私有读)
     *  @param  size        Number          (选填) 图片独有参数, 设置需要的图片宽度 (例: 100)
     */
    downloadFile(name, authType = 'PUBLIC', size) {
        return new Promise(async (resolve, reject) => {
            if (!name) {
                reject(new Error('文件name不能为空'));
                return;
            }

            try {
                const client = await Oss.createClient(authType);
                // doc https://github.com/ali-sdk/ali-oss?spm=a2c4g.11186623.2.11.46fb19fdJYAfum#signatureurlname-options
                // expires单位: 秒
                const opt = { expires: 3600, };
                if (size) opt.process = `image/resize,w_${size}`;

                let url = client.signatureUrl(name, opt);
                // PUBLIC公共文件使用cdn host, 删除签名
                if (authType === 'PUBLIC') {
                    // 替换host为cdn, 注意: cdnHost需要以'/'结尾
                    if (this.PUBLIC.token.cdnHost) url = url.replace(/^http(s)?:\/\/(.*?)\//, this.PUBLIC.token.cdnHost);
                    // 删除签名
                    url = url.replace(/\?.*$/, '');
                    localStorage.setItem('aliCdnHost', url);
                    // 裁剪参数
                    if (size) url += `?x-oss-process=image%2Fresize%2Cw_${size}`;
                }

                resolve(url);
            } catch (err) {
                reject(err);
            }
        });
    }
}

export default new Oss();
